/**
 * Georeferenced image support (image + ESRI world file: .jgw / .pgw / .tfw / .wld)
 *
 * A world file holds six lines:
 *   A  pixel size in x (map units per pixel)
 *   D  rotation term
 *   B  rotation term
 *   E  pixel size in y (negative for north-up images)
 *   C  x of the CENTER of the top-left pixel
 *   F  y of the CENTER of the top-left pixel
 */

// Floor plan coordinate system: 40 pixels = 1 foot
const PLAN_PX_PER_FOOT = 40;
const FEET_PER_METER = 3.280833333; // US survey foot

// Largest side of the image kept in the project. Drone orthophotos can be
// 15k+ pixels square; this keeps the saved project and canvas memory sane.
const MAX_IMAGE_SIDE = 4096;

export const WORLD_FILE_EXTENSIONS = ['jgw', 'pgw', 'tfw', 'wld', 'jpgw', 'pngw', 'tifw'];
export const GEO_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

export const getExtension = (name) => (name.split('.').pop() || '').toLowerCase();
const baseName = (name) => name.replace(/\.[^.]+$/, '').toLowerCase();

export const parseWorldFile = (text) => {
  const values = text.trim().split(/\s+/).map(Number);
  if (values.length < 6 || values.slice(0, 6).some(v => !Number.isFinite(v))) {
    throw new Error('World file must contain six numbers');
  }
  const [a, d, b, e, c, f] = values;
  return { a, d, b, e, c, f };
};

/**
 * Pick the image and its world file out of a multi-file selection.
 * Returns { image, worldFile } (worldFile may be null).
 */
export const pairGeoFiles = (files) => {
  const list = Array.from(files);
  const image = list.find(f => GEO_IMAGE_EXTENSIONS.includes(getExtension(f.name)));
  if (!image) return { image: null, worldFile: null };
  const worlds = list.filter(f => WORLD_FILE_EXTENSIONS.includes(getExtension(f.name)));
  const worldFile = worlds.find(f => baseName(f.name) === baseName(image.name)) || worlds[0] || null;
  return { image, worldFile };
};

/**
 * Load an image + world file and produce data for importPdfLayer.
 * `units` is the world file's linear unit: 'ft' (US survey feet) or 'm'.
 */
export const loadGeoImage = async (imageFile, worldFile, units = 'ft') => {
  const world = parseWorldFile(await worldFile.text());
  if (Math.abs(world.d) > 1e-9 || Math.abs(world.b) > 1e-9) {
    throw new Error('Rotated world files are not supported - export the orthophoto north-up');
  }

  const bitmap = await createImageBitmap(imageFile);
  const sourceWidth = bitmap.width;
  const sourceHeight = bitmap.height;
  const shrink = Math.min(1, MAX_IMAGE_SIDE / Math.max(sourceWidth, sourceHeight));
  const width = Math.round(sourceWidth * shrink);
  const height = Math.round(sourceHeight * shrink);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

  const feetPerUnit = units === 'm' ? FEET_PER_METER : 1;
  const unitsToPlanPx = feetPerUnit * PLAN_PX_PER_FOOT;
  // Map units per pixel of the (possibly downsampled) image we keep
  const unitsPerPixelX = world.a / shrink;
  const unitsPerPixelY = Math.abs(world.e) / shrink;

  return {
    dataUrl,
    // Image pixel size; the layer's drawn size comes from the world file
    imageWidth: width,
    imageHeight: height,
    width: width * unitsPerPixelX * unitsToPlanPx,
    height: height * unitsPerPixelY * unitsToPlanPx,
    // Equivalent of a PDF calibration so measurement tools treat it as scaled:
    // real-world inches per 72 image pixels
    scale: 72 * unitsPerPixelX * feetPerUnit * 12,
    fileName: imageFile.name,
    geo: {
      units,
      unitsToPlanPx,
      // Map coordinates of the image's top-left CORNER (world file gives pixel center)
      originX: world.c - world.a / 2,
      originY: world.f - world.e / 2,
      sourceWidth,
      sourceHeight,
      sourcePixelSize: world.a,
    },
  };
};

/**
 * Convert a floor plan point to map coordinates (easting, northing) using the
 * georeferenced layer. Follows the layer if it has been moved.
 */
export const planToMap = (layer, point) => {
  const { geo, position = { x: 0, y: 0 } } = layer;
  return {
    x: geo.originX + (point.x - position.x) / geo.unitsToPlanPx,
    y: geo.originY - (point.y - position.y) / geo.unitsToPlanPx,
  };
};

export const mapToPlan = (layer, point) => {
  const { geo, position = { x: 0, y: 0 } } = layer;
  return {
    x: position.x + (point.x - geo.originX) * geo.unitsToPlanPx,
    y: position.y + (geo.originY - point.y) * geo.unitsToPlanPx,
  };
};
